/**
 * Pure platform-health label helpers (F-FU-37). Kept free of Angular, RxJS and
 * the generated API barrel so eagerly loaded shell components (the footer) can
 * share the copy without pulling the overview poller into the startup bundle.
 */

export type PlatformHealthVerdict = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

/**
 * All platform-health labels use the backend's categorical rule (F-FU-37):
 * exactly `unknown` means not assessed (for an overall status: no component was
 * assessed). A missing value and legacy `partial` (pre-F-FU-37 backends) carry
 * no verdict either. Any other unrecognised category is an assessment of
 * trouble and reads as `degraded`, never as healthy or as no verdict.
 */
export function platformHealthVerdict(status: string | null | undefined): PlatformHealthVerdict {
  const normalised = status?.trim().toLowerCase() ?? '';
  switch (normalised) {
    case 'healthy': case 'ok': return 'healthy';
    case 'degraded': case 'warning': return 'degraded';
    case 'unhealthy': case 'critical': case 'unavailable': case 'down': case 'error': return 'unhealthy';
    case '': case 'unknown': case 'partial': return 'unknown';
    default: return 'degraded';
  }
}

const ACRONYMS: ReadonlySet<string> = new Set(['api', 'db', 'ws', 'http', 'https', 'grpc', 'fix', 'tls']);
const NAME_OVERRIDES: Readonly<Record<string, string>> = { websocket: 'WebSocket' };

/** `api_gateway` → `API gateway`, `trading_engine` → `Trading engine`, `websocket` → `WebSocket`. */
export function humaniseComponentName(key: string): string {
  const override = NAME_OVERRIDES[key.trim().toLowerCase()];
  if (override) {
    return override;
  }
  return key
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (ACRONYMS.has(lower)) {
        return lower.toUpperCase();
      }
      return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

/**
 * Component names the backend reported as not assessed. Older backends omit the
 * field, so anything that is not an array of non-empty strings reads as none.
 */
export function readUnassessedComponents(response: { unassessed_components?: unknown } | null | undefined): string[] {
  const names = response?.unassessed_components;
  return Array.isArray(names)
    ? names.filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
    : [];
}

/** Shared caveat copy, e.g. `3 not assessed: API gateway, Trading engine, Risk manager`. */
export function describeUnassessedComponents(names: readonly string[]): string | null {
  return names.length === 0 ? null : `${names.length} not assessed: ${names.map(humaniseComponentName).join(', ')}`;
}
